import React__default, { memo, useCallback, useMemo, useState, useContext, useEffect, createElement } from 'react';
import { DateTime } from '@reynolds/datetime';
import { JSXIf, JSXSwitch, JSXCase } from '@framework-controls/jsxconditions';
import classNames from 'classnames';
import muiIconButton__default from '@material-ui/core/IconButton';
import { IconLeftCaret, IconRightCaret } from '@reynolds/icons-common';
import muiIcon__default from '@material-ui/core/Icon';
import Typography from '@material-ui/core/Typography';
import { times } from 'lodash';
import Scrollbar from 'react-scrollbars-custom';

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Calendar-module_calendar__2ltEs {\n  font-family: inherit;\n}\n\n.Calendar-module_sizeFull__1IJ1A {\n  flex: 1;\n  max-width: 400px;\n  max-height: 400px;\n  font-size: inherit;\n}\n\n.Calendar-module_sizeSmall__pYYl8 {\n  padding: 4px 10px;\n  max-width: 250px;\n  max-height: 250px;\n  font-size: 0.75rem;\n}\n\n.Calendar-module_sizeMedium__1OCPf {\n  padding: 4px 10px;\n  max-width: 300px;\n  max-height: 300px;\n  font-size: 0.875rem;\n}\n\n.Calendar-module_sizeLarge__3pcIL {\n  padding: 8px 11px;\n  max-width: 350px;\n  max-height: 350px;\n  font-size: 1rem;\n}\n\n.Calendar-module_legend__2Y0SC {\n  display: block;\n}\n\n.Calendar-module_toolbarButton__2JIs4 {\n  cursor: pointer;\n  font-size: 1.286rem;\n  font-weight: bold;\n  color: #333;\n}\n\n.Calendar-module_title__2XO6s {\n  padding: 0;\n  margin: 0;\n  flex: 0 0 auto;\n  background: white;\n}\n\n.Calendar-module_header__1988Z {\n  display: flex;\n}\n\n.Calendar-module_headerText__nnnRG {\n  flex: 1;\n  margin: auto 1em;\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  justify-content: center;\n  height: 44px;\n  min-height: 44px;\n  color: white;\n}";
var css = {"calendar":"Calendar-module_calendar__2ltEs","sizeFull":"Calendar-module_sizeFull__1IJ1A","sizeSmall":"Calendar-module_sizeSmall__pYYl8","sizeMedium":"Calendar-module_sizeMedium__1OCPf","sizeLarge":"Calendar-module_sizeLarge__3pcIL","legend":"Calendar-module_legend__2Y0SC","toolbarButton":"Calendar-module_toolbarButton__2JIs4","title":"Calendar-module_title__2XO6s","header":"Calendar-module_header__1988Z","headerText":"Calendar-module_headerText__nnnRG"};
styleInject(css_248z,{"insertAt":"top"});

var css_248z$1 = ".DaySelection-module_calendar__3GSgT {\n  max-height: 230px;\n  max-width: 400px;\n  margin: 1.5px;\n}\n\n.DaySelection-module_header__VGwTB {\n  border-bottom: 1px solid lightgrey;\n}\n\n.DaySelection-module_week__TwWD6 {\n  display: flex;\n  justify-content: space-between;\n}\n\n.DaySelection-module_legend__gsfiX {\n  justify-content: space-between;\n  display: flex;\n  vertical-align: middle;\n  margin-top: 0.5rem;\n  font-size: 0.65rem;\n}\n\n.DaySelection-module_today__3BMhy {\n  justify-content: space-between;\n}\n\n.DaySelection-module_todayDot__2OfLx {\n  height: 0.75rem;\n  width: 0.75rem;\n  background-color: var(--currentDayBgColor);\n  border: 1px solid var(--currentDayBorderColor);\n  border-radius: 50%;\n  display: inline-block;\n  margin-right: 0.5rem;\n}\n\n.DaySelection-module_selected__2jHkQ {\n  justify-content: space-between;\n}\n\n.DaySelection-module_selectedDot__1gUAn {\n  height: 0.75rem;\n  width: 0.75rem;\n  background-color: var(--selectedDayBgColor);\n  border: 1px solid var(--selectedDayBorderColor);\n  border-radius: 50%;\n  display: inline-block;\n  margin-right: 0.5rem;\n}";
var css$1 = {"calendar":"DaySelection-module_calendar__3GSgT","header":"DaySelection-module_header__VGwTB","week":"DaySelection-module_week__TwWD6","legend":"DaySelection-module_legend__gsfiX","today":"DaySelection-module_today__3BMhy","todayDot":"DaySelection-module_todayDot__2OfLx","selected":"DaySelection-module_selected__2jHkQ","selectedDot":"DaySelection-module_selectedDot__1gUAn"};
styleInject(css_248z$1,{"insertAt":"top"});

/** This is a wrapper for the Day on a Calendar.  It allows us to capture events when the user
 * interacts with the days.
 */
const DayWrapper = memo((props) => {
    /** Triggered when a day is clicked. */
    const onClick = useCallback(() => {
        props.onSelect(props.value);
    }, [props.onSelect, props.value]);
    return (React__default.createElement("div", { onClick: props.dayInCurrentMonth && !props.disabled && !props.readOnly ? onClick : undefined, onKeyPress: props.dayInCurrentMonth && !props.disabled && !props.readOnly ? onClick : undefined, role: 'presentation' }, props.children));
});

var css_248z$2 = ".Day-module_day__1SO_D {\n  width: 30px;\n  height: 30px;\n  line-height: 1rem;\n  color: var(--textColor);\n  font-weight: 700;\n}\n.Day-module_day__1SO_D .Day-module_day__1SO_D:hover {\n  background-color: var(--actionColor);\n  color: var(--contrastColor);\n}\n.Day-module_day__1SO_D .Day-module_readOnly__1R6ni {\n  pointer-events: none;\n}\n.Day-module_day__1SO_D .Day-module_outlined__3BTxy {\n  border: 2px solid red;\n}\n.Day-module_day__1SO_D .Day-module_hidden__3ZtNX {\n  opacity: 0;\n  pointer-events: none;\n}\n.Day-module_day__1SO_D .Day-module_unavailable__1Kl7s {\n  background-color: var(--altBackingColor);\n  pointer-events: none;\n  color: var(--disabledColor);\n}\n.Day-module_day__1SO_D .Day-module_current__1fbe9 {\n  color: var(--currentDayFontColor);\n  background-color: var(--currentDayBgColor);\n  font-weight: 600;\n  border: 1px solid var(--currentDayBorderColor);\n}\n.Day-module_day__1SO_D .Day-module_selected__2C1xe {\n  color: var(--selectedDayFontColor);\n  background-color: var(--selectedDayBgColor);\n  font-weight: 700;\n  border: 1px solid var(--selectedDayBorderColor);\n}\n.Day-module_day__1SO_D .Day-module_disabled___Nmcs {\n  pointer-events: none;\n  color: var(--disabledColor);\n}";
var css$2 = {"day":"Day-module_day__1SO_D","readOnly":"Day-module_readOnly__1R6ni","outlined":"Day-module_outlined__3BTxy","hidden":"Day-module_hidden__3ZtNX","unavailable":"Day-module_unavailable__1Kl7s","current":"Day-module_current__1fbe9","selected":"Day-module_selected__2C1xe","disabled":"Day-module_disabled___Nmcs"};
styleInject(css_248z$2,{"insertAt":"top"});

/** Represents a single day on the Calendar control. */
const Day = memo((props) => {
    const dayClassNames = classNames(css$2.day, {
        [css$2.hidden]: props.hidden,
        [css$2.disabled]: props.disabled,
        [css$2.unavailable]: props.unavailable,
        [css$2.readOnly]: props.readOnly && !props.unavailable,
        [css$2.current]: props.current,
        [css$2.selected]: props.selected,
    });
    const tabIndex = props.hidden || props.disabled || props.readOnly ? -1 : 0;
    return (
    // <i className={dayClassNames}>
    //     {props.children}
    // </i>
    React__default.createElement("div", { className: dayClassNames },
        React__default.createElement(muiIconButton__default, { className: dayClassNames, tabIndex: tabIndex, size: 'small' },
            React__default.createElement("span", null, props.children))));
});

const DisplayDays = (props) => {
    const days = props.week.map((day) => {
        const disabled = props.shouldDisableDate(day);
        const outlined = props.shouldOutlineDate(day);
        const unavailable = props.shouldUnavailableDate(day);
        const dayInCurrentMonth = day.isSame(props.currentMonth, 'month');
        const isSelected = props.selected.isSame(day, 'day');
        const isToday = day.isToday();
        return (React__default.createElement(DayWrapper, { key: day.format('day'), value: day, dayInCurrentMonth: dayInCurrentMonth, disabled: disabled, onSelect: props.onSelect, readOnly: props.readOnly },
            React__default.createElement(Day, { current: isToday, hidden: !dayInCurrentMonth, disabled: disabled, selected: isSelected, outlined: outlined, readOnly: props.readOnly, unavailable: unavailable }, day.get('date'))));
    });
    return (React__default.createElement(React__default.Fragment, null, days));
};

const DisplayWeeks = (props) => {
    // Get the beginning and end of each month
    const start = props.currentMonth.getFirstOfMonth().getFirstDayOfWeek().getDateOnly();
    const end = props.currentMonth.getEndOfMonth().getLastDayOfWeek().getDateOnly();
    // Create an array for the weeks.  Array[week][days]
    const nestedWeeks = [];
    let count = 0;
    let current = start;
    while (current.isSameOrBefore(end, 'day')) {
        const weekNumber = Math.floor(count / 7);
        nestedWeeks[weekNumber] = nestedWeeks[weekNumber] || [];
        nestedWeeks[weekNumber].push(current);
        current = current.addDays(1);
        count++;
    }
    const weeks = nestedWeeks.map((week) => (React__default.createElement("div", { key: `week-${week[0].format('week')}`, className: css$1.week },
        React__default.createElement(DisplayDays, { week: week, currentMonth: props.currentMonth, selected: props.selected, onSelect: props.onSelect, shouldDisableDate: props.shouldDisableDate, shouldOutlineDate: props.shouldOutlineDate, shouldUnavailableDate: props.shouldUnavailableDate, readOnly: props.readOnly }))));
    return (React__default.createElement(React__default.Fragment, null, weeks));
};

var css_248z$3 = ".DaySelectionHeader-module_headerText__2M2Tu {\n  font-size: 1.5rem;\n  font-weight: bold;\n  color: var(--textColor);\n}\n\n.DaySelectionHeader-module_headerButtons__3hE5m {\n  align-items: flex-end;\n}\n\n.DaySelectionHeader-module_switchHeader__I38B5 {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  margin-bottom: 0.5rem;\n  border-bottom: 1px solid lightgrey;\n}\n\n.DaySelectionHeader-module_daysHeader__DTIkC {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n}\n\n.DaySelectionHeader-module_dayLabel__1KSDt {\n  width: 36px;\n  margin: 0 2px;\n  text-align: center;\n  color: var(--textColor);\n  font-size: 0.875rem;\n}";
var css$3 = {"headerText":"DaySelectionHeader-module_headerText__2M2Tu","headerButtons":"DaySelectionHeader-module_headerButtons__3hE5m","switchHeader":"DaySelectionHeader-module_switchHeader__I38B5","daysHeader":"DaySelectionHeader-module_daysHeader__DTIkC","dayLabel":"DaySelectionHeader-module_dayLabel__1KSDt"};
styleInject(css_248z$3,{"insertAt":"top"});

const MonthsEN = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
const MonthsFR = [
    'Janvier',
    'Fevrier',
    'Mars',
    'Avril',
    'Mai',
    'Juin',
    'Juillet',
    'Août',
    'Septembre',
    'Octobre',
    'Novembre',
    'Decembre',
];

/** Header portion of the DaySelection control.  This includes the current month,
 * day of the weeks and the left and right paging icons.
 */
const DaySelectionHeader = ((props) => {
    const nextMonth = props.currentMonth.addMonths(1);
    const previousMonth = props.currentMonth.addMonths(-1);
    const selectNextMonth = useCallback(() => {
        props.onMonthChange(nextMonth);
    }, [props.onMonthChange, nextMonth]);
    const selectPreviousMonth = useCallback(() => {
        props.onMonthChange(previousMonth);
    }, [previousMonth, props === null || props === void 0 ? void 0 : props.onMonthChange]);
    const month = parseInt(props.currentMonth.format('MM'), 10);
    let monthLabel = MonthsEN[month - 1];
    if (props.locale === 'fr-CA') {
        monthLabel = MonthsFR[month - 1];
    }
    const header = monthLabel + props.currentMonth.format(' YYYY');
    const weekDays = useMemo(() => {
        switch (props.locale) {
            case 'fr-CA':
                if (props.dayLabelType === 'char') {
                    return ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
                }
                else {
                    return ['DIM', 'LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM'];
                }
            default:
                if (props.dayLabelType === 'char') {
                    return ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
                }
                else {
                    return ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
                }
        }
    }, [props === null || props === void 0 ? void 0 : props.dayLabelType, props === null || props === void 0 ? void 0 : props.locale]);
    const prevMonthBtn = useMemo(() => props.disablePrevMonth ? null :
        (React__default.createElement(muiIconButton__default, { disabled: props.disablePrevMonth, onClick: selectPreviousMonth, size: 'small' },
            React__default.createElement(muiIcon__default, null,
                React__default.createElement(IconLeftCaret, null)))), [props.disablePrevMonth, selectPreviousMonth]);
    const nextMontBtn = useMemo(() => props.disableNextMonth ? null :
        (React__default.createElement(muiIconButton__default, { disabled: props.disableNextMonth, onClick: selectNextMonth, size: 'small' },
            React__default.createElement(muiIcon__default, null,
                React__default.createElement(IconRightCaret, null)))), [props.disableNextMonth, selectNextMonth]);
    return (React__default.createElement("div", null,
        React__default.createElement("div", { className: css$3.switchHeader },
            React__default.createElement("span", { className: css$3.headerText }, header),
            React__default.createElement("span", { className: css$3.headerButtons },
                prevMonthBtn,
                nextMontBtn)),
        React__default.createElement("div", { className: css$3.daysHeader }, weekDays.map((day, index) => (React__default.createElement(Typography, { key: index, variant: 'caption', className: css$3.dayLabel }, day))))));
});

const DaySelection = (props) => {
    const [currentMonth, setCurrentMonth] = useState(props.date.getFirstOfMonth());
    const [selected, setSelected] = useState(props.date);
    /** Triggered when the month is changed */
    const onMonthChange = useCallback((newMonth) => {
        setCurrentMonth(newMonth);
        if (props.getOutlinedDays) {
            props.getOutlinedDays(newMonth);
        }
    }, [props === null || props === void 0 ? void 0 : props.getOutlinedDays]);
    /** Checks if the Previous month should be enabled */
    const shouldDisablePrevMonth = useMemo(() => {
        const now = new DateTime();
        const value = ((props === null || props === void 0 ? void 0 : props.disablePast) && now.isAfter(props === null || props === void 0 ? void 0 : props.minDate, 'day')) ? now : props.minDate;
        return value.isAfter(currentMonth, 'day');
    }, [props === null || props === void 0 ? void 0 : props.minDate, props === null || props === void 0 ? void 0 : props.disablePast, currentMonth]);
    /** Checks if the Next month should be enabled */
    const shouldDisableNextMonth = useMemo(() => {
        const now = new DateTime();
        const value = ((props === null || props === void 0 ? void 0 : props.disableFuture) && now.isBefore(props === null || props === void 0 ? void 0 : props.maxDate, 'day')) ? now : props.maxDate;
        return value.isBefore(currentMonth, 'day');
    }, [props === null || props === void 0 ? void 0 : props.maxDate, props === null || props === void 0 ? void 0 : props.disableFuture, currentMonth]);
    /** Validates the selected date is between the min and max dates. */
    const validateMinMaxDate = useCallback((day) => {
        const value = !day.isBetween(props.minDate, props.maxDate, 'day');
        return value;
    }, [props === null || props === void 0 ? void 0 : props.minDate, props === null || props === void 0 ? void 0 : props.maxDate]);
    /** Checks if the given date should be disabled. */
    const shouldDisableDate = useCallback((day) => {
        const now = new DateTime();
        return ((!!props.disablePast && day.isBefore(now, 'day')) ||
            (!!props.disableFuture && day.isAfter(now, 'day')) ||
            validateMinMaxDate(day) ||
            (!!props.shouldDisableDate && props.shouldDisableDate(day)));
    }, [props === null || props === void 0 ? void 0 : props.disablePast, props === null || props === void 0 ? void 0 : props.disableFuture, props === null || props === void 0 ? void 0 : props.shouldDisableDate, validateMinMaxDate]);
    /** Checks the date to see if we need to show an outline */
    const shouldOutlineDate = useCallback((day) => {
        if (!props.outlinedDays || !props.outlinedDays.length) {
            return false;
        }
        const outline = props.outlinedDays.findIndex((x) => x.isSame(day, 'day'));
        return (outline !== -1);
    }, [props === null || props === void 0 ? void 0 : props.outlinedDays]);
    /** Checks the data to see if wee need to show it as unavailable */
    const shouldUnavailableDate = useCallback((day) => {
        if (!props.unavailableDays || !props.unavailableDays.length) {
            return false;
        }
        const unavailable = props.unavailableDays.findIndex((x) => x.isSame(day, 'day'));
        return (unavailable !== -1);
    }, [props === null || props === void 0 ? void 0 : props.unavailableDays]);
    /** Jumps to the specified day. */
    const moveToDay = useCallback((day) => {
        if (day && !shouldDisableDate(day)) {
            setSelected(day);
            props.onChange(day);
        }
    }, [shouldDisableDate, props.onChange]);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(DaySelectionHeader, { currentMonth: currentMonth, onMonthChange: onMonthChange, disablePrevMonth: shouldDisablePrevMonth, disableNextMonth: shouldDisableNextMonth, locale: props.locale, dayLabelType: props.dayLabelType, readOnly: props.readOnly }),
        React__default.createElement("div", { className: css$1.calendar },
            React__default.createElement(DisplayWeeks, { currentMonth: currentMonth, className: css$1.week, selected: selected, onSelect: moveToDay, shouldDisableDate: shouldDisableDate, shouldOutlineDate: shouldOutlineDate, shouldUnavailableDate: shouldUnavailableDate, readOnly: props.readOnly }),
            React__default.createElement("br", null),
            React__default.createElement(JSXIf, { Condition: (props === null || props === void 0 ? void 0 : props.selectedDayLiteral) !== undefined },
                React__default.createElement("div", { className: css$1.legend },
                    React__default.createElement("span", { className: css$1.today },
                        React__default.createElement("span", { className: css$1.todayDot }),
                        React__default.createElement("span", null, "Today's Date")),
                    React__default.createElement("span", { className: css$1.selected },
                        React__default.createElement("span", { className: css$1.selectedDot }),
                        React__default.createElement("span", null, props.selectedDayLiteral)))))));
};

var css_248z$4 = ".MonthSelection-module_container__1aqnM {\n  height: 300px;\n  display: flex;\n  flex-direction: column;\n}\n\n.MonthSelection-module_row__1u-GU {\n  flex-grow: 1;\n  display: flex;\n  align-items: stretch;\n}\n\n.MonthSelection-module_month__2sq2H {\n  flex-grow: 1;\n  flex-basis: 0;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  margin: 5px;\n  border-radius: 5px;\n}\n\n.MonthSelection-module_month__2sq2H:hover {\n  background-color: #BCBCBC;\n}\n\n.MonthSelection-module_selected__2kjl0 {\n  color: white;\n  background-color: #52BF24;\n}\n\n.MonthSelection-module_selected__2kjl0:hover {\n  background-color: #171482;\n}\n\n.MonthSelection-module_disabled__3EQd9 {\n  pointer-events: none;\n  color: #CCCCCC;\n}";
var css$4 = {"container":"MonthSelection-module_container__1aqnM","row":"MonthSelection-module_row__1u-GU","month":"MonthSelection-module_month__2sq2H","selected":"MonthSelection-module_selected__2kjl0","disabled":"MonthSelection-module_disabled__3EQd9"};
styleInject(css_248z$4,{"insertAt":"top"});

const Month = (props) => {
    let langCode = 'en';
    if (props.locale === 'fr-CA') {
        langCode = 'fr';
    }
    const monthName = new DateTime(props.month).getMonthOfYear(langCode);
    const selectedClass = props.selected ? css$4.selected : undefined;
    const disabledClass = props.disabled ? css$4.disabled : undefined;
    const onClick = useCallback(() => {
        props.onSelect(props.month);
    }, [props.onSelect, props.month]);
    return (React__default.createElement("div", { className: `${css$4.month} ${selectedClass} ${disabledClass}`, onClick: onClick }, monthName));
};

/** Renders the Year Selection view for the calendar.  It allows user to select
 * a month then drills back to the date selection.
 */
const MonthSelection = (props) => {
    const onSelect = useCallback((month) => {
        props.onChange(props.date.set('month', month));
    }, [props.onChange, props.date]);
    const currentYear = props.date.get('year');
    const currentMonth = props.date.get('month');
    const now = new DateTime();
    const nowYear = now.get('year');
    const nowMonth = now.get('month');
    const makeMonth = (month) => (React__default.createElement(Month, { key: month, month: month, selected: month === currentMonth, disabled: currentYear === nowYear && ((props.disablePast && month < nowMonth) ||
            (props.disableFuture && month > nowMonth)), onSelect: onSelect, locale: props.locale }));
    return (React__default.createElement("div", { className: css$4.container },
        React__default.createElement("div", { className: css$4.row }, times(4, (i) => makeMonth(i))),
        React__default.createElement("div", { className: css$4.row }, times(4, (i) => makeMonth(i + 4))),
        React__default.createElement("div", { className: css$4.row }, times(4, (i) => makeMonth(i + 8)))));
};

var css_248z$5 = ".YearSelection-module_container__1H16j {\n  justify-content: center;\n  display: flex;\n  flex-direction: row;\n  flex-wrap: wrap;\n}\n\n.YearSelection-module_wrapper__Wmqon {\n  height: 300px;\n}\n\n.YearSelection-module_year__1YgiH {\n  height: 85px;\n  width: 85px;\n  margin: 2.5px;\n  display: flex;\n  box-sizing: border-box;\n  align-items: center;\n  justify-content: center;\n  cursor: pointer;\n  outline: none;\n  flex-basis: 30%;\n  font-size: 1rem;\n  border: 1px solid black;\n  color: black;\n}\n.YearSelection-module_year__1YgiH:hover {\n  color: navy;\n}\n\n.YearSelection-module_selected__2o7dK {\n  color: white;\n  background-color: #52BF24;\n}\n\n.YearSelection-module_disabled__1fSM7 {\n  pointer-events: none;\n  color: gray;\n}";
var css$5 = {"container":"YearSelection-module_container__1H16j","wrapper":"YearSelection-module_wrapper__Wmqon","year":"YearSelection-module_year__1YgiH","selected":"YearSelection-module_selected__2o7dK","disabled":"YearSelection-module_disabled__1fSM7"};
styleInject(css_248z$5,{"insertAt":"top"});

/** Theme context, empty by default. */
const ThemeContext = React__default.createContext({});
/** Provides a theme for use with the `useTheme` hook. */
const ThemeProvider = ThemeContext.Provider;
/** Separates component class name prefixes from variant names. */
const variantSeparator = '_';
/**
 * Adds the given prefix to the given variant name, using `variantSeparator` in between.
 * @param prefix CSS class name prefix
 * @param variant Single variant name
 * @returns Prefixed variant name
 */
const concatVariant = (prefix, variant) => `${prefix}${variantSeparator}${variant}`;
/**
 * If a theme is currently active, applies the theme to a copy of the given CSS.
 * Otherwise, just returns the `css` parameter as given.
 * Memoized.
 *
 * A theme is applied by merging the theme class name table with the `css` table, and returning the result.
 *
 * If an entry is present in `css`, but not the theme, it is copied from `css` to the result table.
 *
 * If an entry is present in the theme, but not in `css`, it is copied from the theme to the result table.
 *
 * If an entry is present in both the theme and `css`, the values are joined together with a space and stored in the result table.
 *
 * For example, given the following theme:
 *
 * ```jsx
 * <ThemeProvider theme={{ 'button': '3zrM9', 'button_horse': 'lkUY5' }}>
 * ```
 *
 * And the following `css` table:
 *
 * ```js
 * const css = {
 *   "button": "1yqRw",
 *   "button_sizeSmall": "LTh7L",
 * };
 * ```
 *
 * Then the result of `useTheme(css)` would be:
 *
 * ```js
 * {
 *   "button": "1yqRw 3zrM9",
 *   "button_sizeSmall": "LTh7L",
 *   'button_horse': "lkUY5",
 * }
 * ```
 *
 * @param css A CSS class name table, usually obtained by importing a CSS file.
 * @returns Either `css` or a copy of `css` with the current theme applied.
 */
const useTheme = (css) => {
    const theme = useContext(ThemeContext);
    const merged = useMemo(() => {
        if (!theme) {
            return css;
        }
        const result = Object.assign({}, css);
        for (const k in theme) {
            if (Object.prototype.hasOwnProperty.call(theme, k)) {
                if (result[k]) {
                    result[k] = `${result[k]} ${theme[k]}`;
                }
                else {
                    result[k] = theme[k];
                }
            }
        }
        return result;
    }, [css, theme]);
    return merged;
};
/**
 * Flattens a Variant array.
 * @param variant An arbitrarily nested Variant array.
 * @returns A flattened array of variant names.
 */
const arrayify = (variant) => {
    if (!variant) {
        return [];
    }
    else if (typeof variant === 'string') {
        return [variant];
    }
    else {
        return variant.map(v => arrayify(v)).reduce((a, v) => a.concat(v), []);
    }
};
/** Conjoins and concatenates variant names into a class name list. See docs for `useClassNames`. */
const conjoinVariants = (theme, name, variant, skipBaseName) => {
    const flatVariant = arrayify(variant);
    const variantClassNames = flatVariant.map(s => concatVariant(name, s));
    const allClassNames = skipBaseName ?
        variantClassNames :
        [name, ...variantClassNames];
    return allClassNames
        .map(s => theme[s])
        .filter(s => s && s.length)
        .join(' ');
};
/**
 * Prepares variant class names. Memoized.
 *
 * Constructs class names by flattening and filtering the `variant` array,
 * and prefixing each variant name with the `name` parameter.
 *
 * The `name` parameter by itself is also included as a class name by default.
 *
 * The resulting names are then converted to themed class names using the `theme` parameter as a dictionary.
 *
 * The obfuscated names are then all joined, separated by spaces, to form the final class name list.
 *
 * For example, if the `theme` table contains the following:
 *
 * ```js
 * const theme = {
 *   "button": "1yqRw"
 *   "button_sizeFull": "R6URd",
 *   "button_sizeSmall": "LTh7L",
 *   "button_sizeLarge": "md1oN",
 * };
 * ```
 *
 * Then calling `useClassNames` with the following arguments:
 *
 * ```js
 * const classnames = useClassNames(theme, 'button', ['sizeSmall']);
 * ```
 *
 * Would result in the class name string `"1yqRw LTh7L"`.
 *
 * The `variant` parameter can be any arbitrarily nested array of variant atoms.
 * Each atom may be either a string (in which case it is included in the output) or one of the values `false` or `undefined`
 * (in which case it is ignored).
 *
 * @param theme A CSS class table, either from importing a CSS file, or from `useTheme()`.
 * @param name Base class name, also used as the variant prefix.
 * @param variant An arbitrarily nested Variant array.
 * @param skipBaseName If true, the non-variant class name based on `name` is not included in the result.
 * @returns Fully concatenated class names, including the base class name and all given variants.
 */
const useClassNames = (theme, name, variant, skipBaseName) => useMemo(() => conjoinVariants(theme, name, variant, skipBaseName), [variant, skipBaseName, name, theme]);

const Year = (props) => {
    /** Triggered when the year is clicked. */
    const onClick = useCallback(() => {
        props.onSelect(props.value);
    }, [props.onSelect, props.value]);
    const theme = useTheme(css$5);
    const classNames = useClassNames(theme, 'year', [
        props.disabled ? 'disabled' : undefined,
        props.selected ? 'selected' : undefined,
    ]);
    return (React__default.createElement(Typography
    // ref={props.getRef}
    , { 
        // ref={props.getRef}
        role: 'button', component: 'div', className: classNames, tabIndex: props.disabled ? -1 : 0, onClick: onClick, onKeyPress: onClick }, props.children));
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var css_248z$6 = ".ScrollArea-module_scrollArea_ScrollbarsCustom__1WBoh {\n  position: relative;\n  width: 100%;\n  height: 100%;\n}\n.ScrollArea-module_scrollArea_ScrollbarsCustom-Wrapper__1_sPt {\n  position: absolute;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n}\n.ScrollArea-module_scrollArea_ScrollbarsCustom-Scroller__xMs5J {\n  position: absolute;\n  top: 0;\n  right: 0;\n  bottom: 0;\n  left: 0;\n}\n.ScrollArea-module_scrollArea_ScrollbarsCustom-Content__E5HK_ {\n  position: relative;\n  box-sizing: border-box;\n  min-width: 100%;\n  min-height: 100%;\n}\n.ScrollArea-module_scrollArea_ScrollbarsCustom-Track__12maE {\n  position: absolute;\n  border-radius: 5px;\n  opacity: 0.3;\n  transition: opacity 0.4s ease-out;\n  background: var(--scrollbarTrack);\n}\n.ScrollArea-module_scrollArea_ScrollbarsCustom__1WBoh:hover .ScrollArea-module_scrollArea_ScrollbarsCustom-Track__12maE {\n  opacity: 1;\n  transition-duration: 0.2s;\n}\n.ScrollArea-module_scrollArea_ScrollbarsCustom-TrackY__1xP1D {\n  width: 5px;\n  height: calc(100% - (2 * 14px));\n  top: 14px;\n  right: calc(14px / 2);\n}\n.ScrollArea-module_scrollArea_ScrollbarsCustom-TrackX__1Np0G {\n  height: 5px;\n  width: calc(100% - (2 * 14px));\n  left: 14px;\n  bottom: calc(14px / 2);\n}\n.ScrollArea-module_scrollArea_ScrollbarsCustom-Thumb__1ReYf {\n  cursor: pointer;\n  border-radius: 5px;\n  background-color: var(--scrollbarThumb);\n}\n.ScrollArea-module_scrollArea_ScrollbarsCustom-ThumbY__1xebT {\n  width: 5px;\n}\n.ScrollArea-module_scrollArea_ScrollbarsCustom-ThumbX__13PcF {\n  height: 5px;\n}\n.ScrollArea-module_scrollArea_trackYVisible__1z2PI .ScrollArea-module_scrollArea_crollbarsCustom-Wrapper__2izW9 {\n  right: 15px;\n}\n.ScrollArea-module_scrollArea_trackXVisible__UwXFY .ScrollArea-module_scrollArea_ScrollbarsCustom-Wrapper__1_sPt {\n  bottom: 15px;\n}\n.ScrollArea-module_scrollArea_dialogScroller__388SZ.ScrollArea-module_trackYVisible__2eLkL > .ScrollArea-module_ScrollbarsCustom-TrackY__tQgLb {\n  margin-right: -24px;\n}";
var css$6 = {"scrollArea_ScrollbarsCustom":"ScrollArea-module_scrollArea_ScrollbarsCustom__1WBoh","scrollArea_ScrollbarsCustom-Wrapper":"ScrollArea-module_scrollArea_ScrollbarsCustom-Wrapper__1_sPt","scrollArea_ScrollbarsCustom-Scroller":"ScrollArea-module_scrollArea_ScrollbarsCustom-Scroller__xMs5J","scrollArea_ScrollbarsCustom-Content":"ScrollArea-module_scrollArea_ScrollbarsCustom-Content__E5HK_","scrollArea_ScrollbarsCustom-Track":"ScrollArea-module_scrollArea_ScrollbarsCustom-Track__12maE","scrollArea_ScrollbarsCustom-TrackY":"ScrollArea-module_scrollArea_ScrollbarsCustom-TrackY__1xP1D","scrollArea_ScrollbarsCustom-TrackX":"ScrollArea-module_scrollArea_ScrollbarsCustom-TrackX__1Np0G","scrollArea_ScrollbarsCustom-Thumb":"ScrollArea-module_scrollArea_ScrollbarsCustom-Thumb__1ReYf","scrollArea_ScrollbarsCustom-ThumbY":"ScrollArea-module_scrollArea_ScrollbarsCustom-ThumbY__1xebT","scrollArea_ScrollbarsCustom-ThumbX":"ScrollArea-module_scrollArea_ScrollbarsCustom-ThumbX__13PcF","scrollArea_trackYVisible":"ScrollArea-module_scrollArea_trackYVisible__1z2PI","scrollArea_crollbarsCustom-Wrapper":"ScrollArea-module_scrollArea_crollbarsCustom-Wrapper__2izW9","scrollArea_trackXVisible":"ScrollArea-module_scrollArea_trackXVisible__UwXFY","scrollArea_dialogScroller":"ScrollArea-module_scrollArea_dialogScroller__388SZ","trackYVisible":"ScrollArea-module_trackYVisible__2eLkL","ScrollbarsCustom-TrackY":"ScrollArea-module_ScrollbarsCustom-TrackY__tQgLb"};
styleInject(css_248z$6,{"insertAt":"top"});

const ScrollArea = (props) => {
    var _a, _b, _c, _d, _e;
    const [scrollbar, setScrollbar] = useState(null);
    const [isAtBottom, setIsAtBottom] = useState(false);
    // Scroll based on the scrollVertical prop.
    useEffect(() => {
        if (props.scrollVertical) {
            if (props.scrollVertical === 'Bottom') {
                setIsAtBottom(true);
                if (scrollbar) {
                    scrollbar.scrollToBottom();
                }
            }
            else if (props.scrollVertical === 'Top') {
                setIsAtBottom(false);
                if (scrollbar) {
                    scrollbar.scrollToTop();
                }
            }
            else if ((!!props.scrollVertical || props.scrollVertical === 0) && scrollbar) {
                scrollbar.scrollTop = props.scrollVertical;
                const sv = scrollbar.getScrollState();
                setIsAtBottom((sv.scrollTop >= (sv.contentScrollHeight - sv.clientHeight)));
            }
        }
    }, [props.scrollVertical, scrollbar]);
    // Scroll based on the scrollHorizontal prop.
    useEffect(() => {
        if (props.scrollHorizontal && scrollbar) {
            if (props.scrollHorizontal === 'Left') {
                scrollbar.scrollToLeft();
            }
            else if (props.scrollHorizontal === 'Right') {
                scrollbar.scrollToRight();
            }
            else if (!!props.scrollHorizontal || props.scrollHorizontal === 0) {
                scrollbar.scrollLeft = props.scrollHorizontal;
            }
        }
    }, [props.scrollHorizontal, scrollbar]);
    // Scroll to the bottom if keepAtBottom is set and we are already at the bottom.
    useEffect(() => {
        if (props.keepAtBottom && scrollbar && isAtBottom) {
            scrollbar.scrollToBottom();
        }
    });
    // Update the scroll state on resize.
    useEffect(() => {
        const onResize = () => {
            if (scrollbar) {
                scrollbar.update(true);
                if (props.onTrackVisible) {
                    const { trackXVisible, trackYVisible } = scrollbar.getScrollState(true);
                    props.onTrackVisible(trackXVisible, trackYVisible);
                }
            }
        };
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
        };
    }, [props === null || props === void 0 ? void 0 : props.onTrackVisible, scrollbar]);
    // Call the onTrackVisible function if indicated.
    useEffect(() => {
        if (props.onTrackVisible && scrollbar) {
            const { trackXVisible, trackYVisible } = scrollbar.getScrollState(true);
            props.onTrackVisible(trackXVisible, trackYVisible);
        }
    }, [props === null || props === void 0 ? void 0 : props.onTrackVisible, scrollbar]);
    // Update the scrollbar state when the ref changes.
    const scrollbarRef = useCallback((node) => {
        if (node !== null) {
            setScrollbar(node);
            if (props.onRef) {
                props.onRef(node);
            }
        }
    }, [props.onRef]);
    // const renderer = React.useCallback(
    //     (x: ElementPropsWithElementRef<HTMLDivElement>) => ScrollbarRoot(x, props.style),
    //     [props?.style]
    // );
    const contentRenderer = useCallback((x) => { var _a; return ScrollbarContent(x, (_a = props.classes) === null || _a === void 0 ? void 0 : _a.content); }, [(_a = props === null || props === void 0 ? void 0 : props.classes) === null || _a === void 0 ? void 0 : _a.content]);
    const trackYRenderer = useCallback((x) => { var _a; return ScrollbarTrackY(x, (_a = props.classes) === null || _a === void 0 ? void 0 : _a.trackY); }, [(_b = props.classes) === null || _b === void 0 ? void 0 : _b.trackY]);
    const trackXRenderer = useCallback((x) => { var _a; return ScrollbarTrackX(x, (_a = props.classes) === null || _a === void 0 ? void 0 : _a.trackX); }, [(_c = props.classes) === null || _c === void 0 ? void 0 : _c.trackX]);
    const wrapperRenderer = useCallback((x) => { var _a; return ScrollbarWrapper(x, (_a = props.classes) === null || _a === void 0 ? void 0 : _a.wrapper); }, [(_d = props.classes) === null || _d === void 0 ? void 0 : _d.wrapper]);
    // const scrollerRenderer = React.useCallback(ScrollbarScroller, []);
    const onScrollImpl = props.onScroll;
    const onScroll = useCallback((sv) => {
        setIsAtBottom((sv.scrollTop >= (sv.contentScrollHeight - sv.clientHeight)));
        if (onScrollImpl) {
            onScrollImpl(sv.scrollTop, sv.scrollLeft);
        }
    }, [onScrollImpl]);
    let componentClassname = 'scrollArea';
    if (!!((_e = props.classes) === null || _e === void 0 ? void 0 : _e.root)) {
        componentClassname = props.classes.root;
    }
    if (!!props.isInDialog) {
        componentClassname = componentClassname + ' scrollArea_dialogScroller';
    }
    return (createElement(Scrollbar, { ref: scrollbarRef, className: componentClassname, style: props.style, removeTracksWhenNotUsed: true, noDefaultStyles: true, translateContentSizesToHolder: props.useContentSize, translateContentSizeXToHolder: props.useContentSizeWidth, translateContentSizeYToHolder: props.useContentSizeHeight, rtl: props.rtl, noScrollX: props.noHorizontalScrollbar, noScrollY: props.noVerticalScrollbar, onScroll: onScroll, 
        // renderer={renderer}
        contentProps: { renderer: contentRenderer }, trackYProps: { renderer: trackYRenderer }, trackXProps: { renderer: trackXRenderer }, wrapperProps: { renderer: wrapperRenderer }, 
        // scrollerProps={{ renderer: scrollerRenderer }}
        trackClickBehavior: 'jump' }, props.children));
};
const ScrollbarContent = (scrollProps, custom) => {
    const { elementRef } = scrollProps, other = __rest(scrollProps, ["elementRef"]);
    const theme = useTheme(css$6);
    const className = useClassNames(theme, 'scrollArea', ['ScrollbarsCustom-Content', custom]);
    return createElement("div", Object.assign({}, other, { ref: elementRef, className: className }));
};
const ScrollbarTrackY = (scrollProps, custom) => {
    const { elementRef } = scrollProps, other = __rest(scrollProps, ["elementRef"]);
    const theme = useTheme(css$6);
    const className = useClassNames(theme, 'scrollArea', ['ScrollbarsCustom-Track', 'ScrollbarsCustom-TrackY', custom]);
    return createElement("div", Object.assign({}, other, { ref: elementRef, className: className }));
};
const ScrollbarTrackX = (scrollProps, custom) => {
    const { elementRef } = scrollProps, other = __rest(scrollProps, ["elementRef"]);
    const theme = useTheme(css$6);
    const className = useClassNames(theme, 'scrollArea', ['ScrollbarsCustom-Track', 'ScrollbarsCustom-TrackX', custom]);
    return createElement("div", Object.assign({}, other, { ref: elementRef, className: className }));
};
const ScrollbarWrapper = (scrollProps, custom) => {
    const { elementRef } = scrollProps, other = __rest(scrollProps, ["elementRef"]);
    const theme = useTheme(css$6);
    const className = useClassNames(theme, 'scrollArea', ['ScrollbarsCustom-Wrapper', custom]);
    return createElement("div", Object.assign({}, other, { ref: elementRef, className: className, style: { overflow: 'hidden' } }));
};

/** Renders the Year Selection view for the calendar.  It allows user to select
 * a year then drills back to the date selection.
 */
const YearSelection = (props) => {
    // const selectedYearRef = React.useRef<HTMLElement>(null);
    const currentYear = props.date.get('year');
    const minYear = props.minDate.get('year');
    const maxYear = props.maxDate.get('year');
    const nowYear = new DateTime().get('year');
    const years = [];
    for (let index = minYear; index < maxYear; index++) {
        years.push(index);
    }
    /** Triggered when a year is selected. */
    const onYearSelect = useCallback((year) => {
        props.onChange(props.date.set('year', year));
    }, [props.onChange, props.date]);
    /** Sets the ref. */
    // const getSelectedYearRef = (event: React.MouseEvent<HTMLElement>) => {
    //     selectedYearRef.current = event.currentTarget;
    // };
    /** Select the current year. */
    // const scrollToCurrentYear = () => {
    //     if (selectedYearRef) {
    //         selectedYearRef.current.scrollIntoView();
    //     }
    // }
    const displayYears = years.map((year) => {
        const selected = year === currentYear;
        const disable = (props.disablePast && year < nowYear) || (props.disableFuture && year > nowYear);
        return (React__default.createElement(Year, { key: year.toString(), selected: selected, disabled: disable, value: year, onSelect: onYearSelect }, year.toString()));
    });
    return (React__default.createElement("div", { className: css$5.wrapper },
        React__default.createElement(ScrollArea, { allowOuterScroll: true, noHorizontalScrollbar: true },
            React__default.createElement("div", { className: css$5.container }, displayYears))));
};

var Mode;
(function (Mode) {
    Mode["DAY"] = "DAY";
    Mode["MONTH"] = "MONTH";
    Mode["YEAR"] = "YEAR";
})(Mode || (Mode = {}));
const Calendar = (props) => {
    // const theme = useTheme(css);
    const minDate = new DateTime('1970-01-01');
    const maxDate = new DateTime('2100-12-31');
    const today = new DateTime();
    const selectedDate = (props.date && props.date.isValid() && props.date.isBetween(minDate, maxDate)) ?
        props.date.getDateOnly() :
        today.getDateOnly();
    // Setup state
    const [selected, setSelected] = useState(selectedDate);
    const [selectionMode, setSelectionMode] = useState(Mode.DAY);
    const [scrolltoDate, setScrollToDate] = useState(false);
    const showDaySelection = useCallback(() => {
        switch (selectionMode) {
            case Mode.DAY:
                if (props.allowMonth) {
                    setSelectionMode(Mode.MONTH);
                }
                break;
            default:
                if (props.allowDay) {
                    setSelectionMode(Mode.DAY);
                }
                else if (props.allowMonth) {
                    setSelectionMode(Mode.MONTH);
                }
                break;
        }
    }, [props.allowMonth, props.allowDay, selectionMode]);
    // const showYearSelection = React.useCallback(() => {
    //     setSelectionMode(Mode.YEAR);
    // }, []);
    const showMonthSelection = useCallback(() => {
        setSelectionMode(Mode.MONTH);
    }, []);
    const onDateSelect = useCallback((day, scrollDate) => {
        if (!props.readOnly) {
            setSelected(day);
            setScrollToDate(scrollDate === true);
            if (props.onChange) {
                props.onChange(day);
            }
        }
    }, [props === null || props === void 0 ? void 0 : props.onChange, props === null || props === void 0 ? void 0 : props.readOnly]);
    const onMonthSelect = useCallback((newDate) => {
        if (!props.readOnly) {
            setSelected(newDate);
            if (props.allowDay !== false) {
                showDaySelection();
            }
            else {
                onDateSelect(newDate);
            }
            // Check for outlined Days
            if (props.getOutlinedDays) {
                props.getOutlinedDays(newDate);
            }
        }
    }, [props === null || props === void 0 ? void 0 : props.allowDay, props === null || props === void 0 ? void 0 : props.getOutlinedDays, onDateSelect, showDaySelection, props === null || props === void 0 ? void 0 : props.readOnly]);
    /** Triggered when the year is changed. */
    const onYearSelect = useCallback((newDate) => {
        if (!props.readOnly) {
            setSelected(newDate);
            if (props.allowMonth) {
                showMonthSelection();
            }
            else if (props.allowDay !== false) {
                showDaySelection();
            }
            else {
                onDateSelect(newDate);
            }
            // Check for outlined Days
            if (props.getOutlinedDays) {
                props.getOutlinedDays(newDate);
            }
        }
    }, [props === null || props === void 0 ? void 0 : props.allowMonth, props === null || props === void 0 ? void 0 : props.allowDay, props === null || props === void 0 ? void 0 : props.getOutlinedDays, showDaySelection,
        showMonthSelection,
        onDateSelect, props === null || props === void 0 ? void 0 : props.readOnly,
    ]);
    const labelType = (props === null || props === void 0 ? void 0 : props.dayLabelType) ? props.dayLabelType : 'char';
    const locale = (props === null || props === void 0 ? void 0 : props.locale) ? props.locale : 'en-US';
    const calSize = (props === null || props === void 0 ? void 0 : props.size) ? props.size : 'full';
    const calClassNames = classNames(css.calendar, {
        [css.sizeFull]: calSize === 'full',
        [css.sizeSmall]: calSize === 'small',
        [css.sizeMedium]: calSize === 'medium',
        [css.sizeLarge]: calSize === 'large',
    });
    return (React__default.createElement("div", { className: calClassNames },
        React__default.createElement(JSXSwitch, null,
            React__default.createElement(JSXCase, { Condition: selectionMode === Mode.YEAR },
                React__default.createElement(YearSelection, { date: selected, onChange: onYearSelect, minDate: minDate, maxDate: maxDate, disablePast: props.disablePast, disableFuture: props.disableFuture })),
            React__default.createElement(JSXCase, { Condition: selectionMode === Mode.MONTH },
                React__default.createElement(MonthSelection, { date: selected, onChange: onMonthSelect, disablePast: props.disablePast, disableFuture: props.disableFuture, locale: props.locale })),
            React__default.createElement(JSXCase, { Default: true },
                React__default.createElement(DaySelection, { date: selected, scrollToDate: scrolltoDate, onChange: onDateSelect, minDate: minDate, maxDate: maxDate, disablePast: props.disablePast, disableFuture: props.disableFuture, outlinedDays: props.outlinedDays, locale: locale, dayLabelType: labelType, selectedDayLiteral: props.selectedDayLiteral, readOnly: props.readOnly, unavailableDays: props.unavailableDays, getOutlinedDays: onMonthSelect })))));
};

export { Calendar, ThemeProvider, useClassNames, useTheme };
